import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogIn, ShieldAlert } from "lucide-react";
import AdminDashboard from "@/pages/AdminDashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [auth, setAuth] = useState(null); // null=checking, false=logged out, object=authed
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setAuth(data))
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setAuth(data);
      toast.success("Welcome back, Commander");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore network errors on logout
    }
    setAuth(false);
  };

  if (auth === null) {
    return (
      <div className="grid min-h-screen place-items-center bg-obsidian">
        <Loader2 className="h-8 w-8 animate-spin text-gold" data-testid="admin-loading" />
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="grid min-h-screen place-items-center bg-obsidian px-4">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-plate/70 p-8 backdrop-blur">
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src="/logo-mark.png"
              alt="Astra Consult logo"
              className="h-16 w-16 rounded-xl border border-white/10 object-cover"
            />
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
              Astra Command
            </p>
            <h1 className="mt-2 font-display text-2xl font-bold text-white">
              Enquiry Triage Panel
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Private access — authorized operators only.
            </p>
          </div>

          <form data-testid="admin-login-form" onSubmit={login} className="space-y-4">
            <div>
              <label htmlFor="a-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Admin email
              </label>
              <input
                id="a-email"
                type="email"
                required
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@astraconsulting.com"
                className="w-full rounded-lg border border-white/10 bg-abyss/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
              />
            </div>
            <div>
              <label htmlFor="a-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Password
              </label>
              <input
                id="a-password"
                type="password"
                required
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full rounded-lg border border-white/10 bg-abyss/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-gold/60 focus:ring-2 focus:ring-gold/20"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
              >
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit-button"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-6 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_40px_rgba(226,185,92,0.4)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
              {busy ? "Authenticating…" : "Enter command"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return <AdminDashboard user={auth} onLogout={logout} />;
}
